// RUN-EFFORT: Setup › Model policy › Effort. The tier → model map runs use (`low | medium | high`
// and `vision`, for work with images), whether the decide classifier may refine an automatic
// pick, and per-agent overrides (a DE's or a helper's own default effort and tier models).
// Admin-only (`manager`: owners, admins, the agency's operators); stored in
// `company.settings.run_effort`, read by run admission and sent to runtimes as a hint.
import { z } from "zod";
import { RunEffortTier } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { RunEffortAgentOverride, RunEffortSettings, RunEffortTierSpec } from "../rows";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const core = { feature: "core", tag: "model-policy" } as const;

/** One tier as it applies now: its model and reasoning, and whether the gateway routes it. */
export const RunEffortTierView = RunEffortTierSpec.extend({
  tier: RunEffortTier,
  /** The company stored this tier (false: the platform's default). */
  custom: z.boolean(),
  /** Routable under the model policy; null when the gateway lists no models. */
  routable: z.boolean().nullable(),
}).meta({ id: "RunEffortTierView" });

/** An agent the page can set an override for: a DE or a helper. */
export const RunEffortAgent = z
  .object({
    id: Uuid,
    kind: z.enum(["digital_employee", "helper"]),
    name: z.string(),
    override: RunEffortAgentOverride.nullable(),
  })
  .meta({ id: "RunEffortAgent" });

export const RunEffortView = z
  .object({
    tiers: z.array(RunEffortTierView),
    /** The platform's defaults (what a tier left out of the map uses). */
    defaults: z.array(RunEffortTierSpec.extend({ tier: RunEffortTier })),
    refine_with_classifier: z.boolean(),
    agents: z.array(RunEffortAgent),
    /** More agents exist than the page lists (500). */
    truncated: z.boolean(),
  })
  .meta({ id: "RunEffortView" });
export type RunEffortView = z.infer<typeof RunEffortView>;

// --- the platform's defaults (Operator console › Effort defaults) ---------------------------------
//
// The tier → model defaults every company starts from: an operator setting (platform admins), kept
// as data with its history (`cp.effort_default`, one row per version, append-only). A company's
// Model policy page overrides them tier by tier. Nothing stored: the built-in map applies.

/** The four tiers, all required (the defaults are a complete map). */
export const EffortDefaultTiers = z
  .strictObject({
    low: RunEffortTierSpec,
    medium: RunEffortTierSpec,
    high: RunEffortTierSpec,
    vision: RunEffortTierSpec,
  })
  .meta({ id: "EffortDefaultTiers" });
export type EffortDefaultTiers = z.infer<typeof EffortDefaultTiers>;

export const EffortDefaultsVersion = z
  .object({
    version: z.int().min(1),
    tiers: EffortDefaultTiers,
    created_at: Timestamp,
    created_by_user_id: Uuid.nullable(),
  })
  .meta({ id: "EffortDefaultsVersion" });

export const EffortDefaultsView = z
  .object({
    /** The defaults in force. */
    tiers: EffortDefaultTiers,
    /** The built-in map (what applies with nothing stored). */
    builtin: EffortDefaultTiers,
    /** The version in force; null: the built-in map. */
    version: z.int().min(1).nullable(),
    /** Newest first (at most 50). */
    history: z.array(EffortDefaultsVersion),
    /** How long another API process may keep serving the previous defaults. */
    cache_seconds: z.int().min(0),
  })
  .meta({ id: "EffortDefaultsView" });
export type EffortDefaultsView = z.infer<typeof EffortDefaultsView>;

export const EffortDefaultsPut = z
  .strictObject({ tiers: EffortDefaultTiers })
  .meta({ id: "EffortDefaultsPut" });
export type EffortDefaultsPut = z.infer<typeof EffortDefaultsPut>;

const operator = { feature: "core", tag: "operator", access: "operator" } as const;

export const effortDefaultsRoutes = {
  "operator.effortDefaults.get": defineRoute({
    ...operator,
    id: "operator.effortDefaults.get",
    method: "get",
    path: "/v1/operator/effort-defaults",
    summary: "The platform's effort tier defaults in force, the built-in map and their history",
    response: { status: 200, schema: EffortDefaultsView },
    errors: [],
    emits: [],
  }),
  "operator.effortDefaults.put": defineRoute({
    ...operator,
    id: "operator.effortDefaults.put",
    method: "put",
    path: "/v1/operator/effort-defaults",
    summary: "Store a new version of the platform's effort tier defaults",
    description:
      "Every company's tiers fall back to these (a company's Model policy overrides them). " +
      "Each change is a new version; the same map again stores nothing.",
    body: EffortDefaultsPut,
    response: { status: 200, schema: EffortDefaultsView },
    errors: [],
    emits: ["effort_defaults.changed"],
  }),
} as const;

export const runEffortRoutes = {
  "runEffort.get": defineRoute({
    ...core,
    id: "runEffort.get",
    method: "get",
    path: `${co}/run-effort`,
    summary: "The effort tiers' models, the classifier switch and per-agent overrides",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: RunEffortView },
    errors: [],
    emits: [],
  }),
  "runEffort.put": defineRoute({
    ...core,
    id: "runEffort.put",
    method: "put",
    path: `${co}/run-effort`,
    summary: "Replace the effort tiers' models, the classifier switch and per-agent overrides",
    description:
      "A tier left out keeps its built-in model. `for_agent` keys are DE memberships or helpers " +
      "of the company. Runs admitted from then on use it.",
    access: "manager",
    params: CompanyParams,
    body: RunEffortSettings,
    response: { status: 200, schema: RunEffortView },
    errors: ["validation_failed"],
    emits: ["company.updated"],
  }),
} as const;
